import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  BankTransactionsEnabledAtEligibilityDTO,
  ClaimResponseDeadlineExtensionPreviewDTO,
  ClaimsApi,
  EvidenceChasersApi,
  OneTimeSessionTokenDTO,
  UsersApi,
} from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { appConfig } from '../../appConfig';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { internalSessionRequestOptions } from '../auth/internal-session';
import {
  CREATE_EVIDENCE_CHASERS_API_FAILED,
  CREATE_EVIDENCE_CHASERS_API_REQUESTED,
  CREATE_EVIDENCE_CHASERS_API_SUCCESS,
  CREATE_EVIDENCE_CHASERS_STORE_KEY,
  EXTEND_RESPONSE_DEADLINE_MODAL_STORE_KEY,
  EXTEND_RESPONSE_DEADLINE_STORE_KEY,
  fetchClaimRequested,
  GENERATE_ADR_FORM_STORE_KEY,
  OPEN_EVIDENCE_WORKSPACE_STORE_KEY,
  WAIVE_CLAIM_DISPUTE_FEE_STORE_KEY,
} from '../claim/claim.actions';
import { fetchClaimResponseDeadlineExtensionsThunk } from '../claim-response-deadline-extensions/claim-response-deadline-extensions.thunk';
import { setFlashMessage } from '../flash-messages/flash-messages.actions';
import { AppState } from '../root.reducer';
import {
  createStandardClaimsApi,
  createStandardEvidenceChasersApi,
  createStandardUsersApi,
  runThunkWithAuth,
} from '../utils/api.utils';
import {
  CreateADRFormPayload,
  CreateEvidenceChasersPayload,
  ExtendResponseDeadlinePayload,
  OpenEvidenceWorkspacePayload,
  PreviewResponseDeadlineExtensionPayload,
  WaiveClaimDisputeFeePayload,
} from './claim.types';

export const waiveClaimDisputeFeeThunk = createAsyncThunk<
  void,
  WaiveClaimDisputeFeePayload,
  {
    state: AppState;
  }
>('payment-plan/create', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: ClaimsApi = createStandardClaimsApi(state, dispatch);
    const { claimId } = payload;

    await runThunkWithAuth(() => api.waiveDisputeFee(claimId), dispatch);

    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: WAIVE_CLAIM_DISPUTE_FEE_STORE_KEY,
        message: `Success! Dispute fee waived for ${claimId}`,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: WAIVE_CLAIM_DISPUTE_FEE_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
});

export const extendResponseDeadlineThunk = createAsyncThunk<
  void,
  ExtendResponseDeadlinePayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('claim/extend-response-deadline', async ({ claimId, ...extension }, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: ClaimsApi = createStandardClaimsApi(state, dispatch);
    await runThunkWithAuth(() => api.extendClaimResponseDeadline(claimId, extension), dispatch);

    // Refresh the claim, whose proposals and status may have changed, and the extensions history.
    dispatch(fetchClaimRequested({ claimId }));
    dispatch(fetchClaimResponseDeadlineExtensionsThunk({ claimId }));
    dispatch(
      setFlashMessage({
        key: EXTEND_RESPONSE_DEADLINE_STORE_KEY,
        message: 'Success! Response deadline extended',
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: EXTEND_RESPONSE_DEADLINE_MODAL_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(error);
  }
});

export const previewResponseDeadlineExtensionThunk = createAsyncThunk<
  ClaimResponseDeadlineExtensionPreviewDTO,
  PreviewResponseDeadlineExtensionPayload,
  {
    state: AppState;
    rejectValue: string;
  }
>('claim/preview-response-deadline-extension', async ({ claimId, responseDeadline }, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: ClaimsApi = createStandardClaimsApi(state, dispatch);
    const response: AxiosResponse<ClaimResponseDeadlineExtensionPreviewDTO> = await runThunkWithAuth(
      () => api.dryRunExtendClaimResponseDeadline(claimId, { responseDeadline }),
      dispatch,
    );
    return response.data;
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: EXTEND_RESPONSE_DEADLINE_MODAL_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(error);
  }
});

export const generateADRFormThunk = createAsyncThunk<
  void,
  CreateADRFormPayload,
  {
    state: AppState;
  }
>('claim/adr-form', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  try {
    const api: ClaimsApi = createStandardClaimsApi(state, dispatch);
    const { claimId } = payload;

    await runThunkWithAuth(() => api.generateADRForm(claimId), dispatch);

    dispatch(fetchClaimRequested({ claimId }));
    dispatch(
      setFlashMessage({
        key: GENERATE_ADR_FORM_STORE_KEY,
        message: `Success! ADR form for ${claimId} will be sent to Slack`,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: GENERATE_ADR_FORM_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
});

export const createEvidenceChasersThunk = createAsyncThunk<
  void,
  CreateEvidenceChasersPayload,
  {
    state: AppState;
  }
>('claim/evidence-chasers', async (payload, thunkAPI) => {
  const { newAdminUrl } = appConfig;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;

  // Dispatch API_REQUESTED action for loading state compatibility
  dispatch({ type: CREATE_EVIDENCE_CHASERS_API_REQUESTED });

  try {
    const evidenceChasersApi: EvidenceChasersApi = createStandardEvidenceChasersApi(state, dispatch);
    const usersApi: UsersApi = createStandardUsersApi(state, dispatch);
    const { claimId, shouldCreate } = payload;
    if (shouldCreate) {
      await runThunkWithAuth(() => evidenceChasersApi.createEvidenceChaser(claimId), dispatch);
    }
    const {
      data: { token },
    }: AxiosResponse<OneTimeSessionTokenDTO> = await runThunkWithAuth(
      () => usersApi.createOneTimeSessionToken(internalSessionRequestOptions),
      dispatch,
    );

    // route to new admin first
    window.open(`${newAdminUrl}/auth/transfer-session?token=${token}&claimId=${claimId}`, '_blank');

    // reload current page to remove the modal after a small delay
    // we have to do it here as we're routing away from the page
    setTimeout(() => {
      window.location.reload();
    }, 100);

    // Dispatch API_SUCCESS action for loading state compatibility
    dispatch({ type: CREATE_EVIDENCE_CHASERS_API_SUCCESS });
  } catch (e) {
    // Dispatch API_FAILED action for loading state compatibility
    dispatch({ type: CREATE_EVIDENCE_CHASERS_API_FAILED });

    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: CREATE_EVIDENCE_CHASERS_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
});

export function evidenceWorkspacePath(claimId: string, documentId?: string): string {
  const workspace = `/claims/${claimId}/evidence-workspace`;
  if (!documentId) {
    return workspace;
  }
  return `${workspace}/${encodeURIComponent(documentId)}`;
}

export function evidenceWorkspaceTransferSessionUrl(
  newAdminUrl: string,
  token: string,
  claimId: string,
  documentId?: string,
): string {
  return `${newAdminUrl}/auth/transfer-session?token=${token}&redirectUrl=${evidenceWorkspacePath(claimId, documentId)}`;
}

export const openEvidenceWorkspaceThunk = createAsyncThunk<
  void,
  OpenEvidenceWorkspacePayload,
  {
    state: AppState;
  }
>('claim/open-evidence-workspace', async (payload, thunkAPI) => {
  const { newAdminUrl } = appConfig;
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;

  try {
    const usersApi: UsersApi = createStandardUsersApi(state, dispatch);
    const {
      data: { token },
    }: AxiosResponse<OneTimeSessionTokenDTO> = await runThunkWithAuth(
      () => usersApi.createOneTimeSessionToken(internalSessionRequestOptions),
      dispatch,
    );

    window.open(evidenceWorkspaceTransferSessionUrl(newAdminUrl, token, payload.claimId, payload.documentId), '_blank');
  } catch (e) {
    const error = getErrorMessage(e);
    dispatch(
      setFlashMessage({
        key: OPEN_EVIDENCE_WORKSPACE_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
});

export const fetchBankTransactionsEnabledAtEligibilityThunk = createAsyncThunk<
  BankTransactionsEnabledAtEligibilityDTO,
  { claimId: string },
  {
    state: AppState;
  }
>('claim/bank-transactions-enabled-at-eligibility', async ({ claimId }, thunkAPI) => {
  const state = thunkAPI.getState();
  const dispatch = thunkAPI.dispatch;
  const api: ClaimsApi = createStandardClaimsApi(state, dispatch);

  const apiResponse: AxiosResponse<BankTransactionsEnabledAtEligibilityDTO> = await runThunkWithAuth(
    () => api.getBankTransactionsEnabledAtEligibility(claimId),
    dispatch,
  );
  return apiResponse.data;
});
